import { HeroEntity } from '@generated/gql';
import { GET_CORE_ADDRESSES, geUriForName } from '@shared/constants/addresses/addresses.constant';

export enum HEROES {
  UNKNOWN,
  THRALL,
  SAVAGE,
  MAGE,
  ASSASSIN,
  GHOST,
  HAMMERGINA,
}

export enum HERO_GRADE {
  BASIC,
  ADVANCED,
  EPIC,
}

export const HEROES_CLASSES_ICONS = {
  [HEROES.UNKNOWN]: 'unknown',
  [HEROES.THRALL]: 'thrall',
  [HEROES.SAVAGE]: 'savage',
  [HEROES.MAGE]: 'mage',
  [HEROES.ASSASSIN]: 'assassin',
  [HEROES.GHOST]: 'ghost',
  [HEROES.HAMMERGINA]: 'hammergina',
};

export const HEROES_CLASSES_ID = {
  [HEROES.UNKNOWN]: 'unknown',
  [HEROES.THRALL]: 'thrall',
  [HEROES.SAVAGE]: 'savage',
  [HEROES.MAGE]: 'mage',
  [HEROES.ASSASSIN]: 'assassin',
  [HEROES.GHOST]: 'ghost',
  [HEROES.HAMMERGINA]: 'hammergina',
};

export const HEROES_GRADE_ID = {
  [HERO_GRADE.BASIC]: 'basic',
  [HERO_GRADE.ADVANCED]: 'advanced',
  [HERO_GRADE.EPIC]: 'epic',
};

export const HEROES_CLASSES_NAMES = {
  [HEROES.UNKNOWN]: 'heroes-classes.unknown',
  [HEROES.THRALL]: 'heroes-classes.thrall',
  [HEROES.SAVAGE]: 'heroes-classes.savage',
  [HEROES.MAGE]: 'heroes-classes.mage',
  [HEROES.ASSASSIN]: 'heroes-classes.assassin',
  [HEROES.GHOST]: 'heroes-classes.ghost',
  [HEROES.HAMMERGINA]: 'heroes-classes.hammergina',
};

export interface HeroesSpecs {
  damage: number;
  survival: number;
  complex: number;
}

export const HEROES_SPECS: { [key: string]: HeroesSpecs } = {
  [HEROES.UNKNOWN]: { damage: 0, survival: 0, complex: 0 },
  [HEROES.THRALL]: { damage: 0.3, survival: 0.8, complex: 0.3 },
  [HEROES.SAVAGE]: { damage: 0.8, survival: 0.3, complex: 0.4 },
  [HEROES.MAGE]: { damage: 0.9, survival: 0.2, complex: 1 },
  [HEROES.ASSASSIN]: { damage: 0.4, survival: 0.5, complex: 0.6 },
  [HEROES.GHOST]: { damage: 1, survival: 1, complex: 0 },
  [HEROES.HAMMERGINA]: { damage: 0.8, survival: 0.9, complex: 0.2 },
};

export const HEROES_CLASSES_NAMES_MAP = new Map<HEROES, string>([
  [HEROES.UNKNOWN, 'heroes-classes.all'],
  [HEROES.THRALL, 'heroes-classes.thrall'],
  [HEROES.SAVAGE, 'heroes-classes.savage'],
  [HEROES.MAGE, 'heroes-classes.mage'],
  [HEROES.ASSASSIN, 'heroes-classes.assassin'],
  [HEROES.GHOST, 'heroes-classes.ghost'],
  [HEROES.HAMMERGINA, 'heroes-classes.hammergina'],
]);

export const getAvatarByHeroClass = (heroClass: number): string => {
  const heroClassName = HEROES_CLASSES_ICONS[heroClass];

  return `assets/images/ui/avatars/heroes/${heroClassName.toLowerCase()}.avif`;
};

export const getAvatarByHero = (hero: HeroEntity): string => {
  return prepareHeroAvatarImageUrl(hero);
};

export const getHeroClassName = (heroClass: number): string => {
  return HEROES_CLASSES_ID[heroClass];
};

export const getHeroGradeName = (heroGrade: number): string => {
  return HEROES_GRADE_ID[heroGrade];
};

export const HEROES_CLASSES_SHORT_DESC_MAP = new Map<HEROES, string>([
  [HEROES.THRALL, 'heroes-classes-short-description.thrall'],
  [HEROES.SAVAGE, 'heroes-classes-short-description.savage'],
  [HEROES.MAGE, 'heroes-classes-short-description.mage'],
  [HEROES.ASSASSIN, 'heroes-classes-short-description.assassin'],
  [HEROES.GHOST, 'heroes-classes-short-description.ghost'],
  [HEROES.HAMMERGINA, 'heroes-classes-short-description.hammergina'],
]);

export function isPrepaymentHero(hero: HeroEntity, chainId: number): boolean {
  try {
    const gameToken = GET_CORE_ADDRESSES(chainId).gameToken;
    return hero.lastPayToken && hero.lastPayToken.id.toLowerCase() !== gameToken.toLowerCase();
  } catch {
    return false;
  }
}

export function lostProfitPercent(maxBiome: number, maxNgLevel: number, heroNgLevel: number): number {
  let percent = 0;
  for (let ngLvl = 0; ngLvl < heroNgLevel; ngLvl++) {
    for (let biome = 1; biome <= maxBiome; biome++) {
      percent += calculateDiscount(maxBiome, maxNgLevel, biome, ngLvl);
    }
  }
  return percent;
}

export function calculateDiscount(maxBiome: number, maxNgLvl: number, biome: number, ngLvl: number): number {
  return (biome * (1 + ngLvl)) / ((maxBiome * (maxBiome + 1)) / 2) / calculateNgSum(maxNgLvl);
}

function calculateNgSum(maxNlLvl: number): number {
  return ((maxNlLvl + 1) * (maxNlLvl + 2)) / 2;
}

export function prepareHeroAvatarImageUrl(hero: HeroEntity): string {
  const mappedHero = heroesMap.get(hero.meta.heroClass);
  if (mappedHero) {
    const stats = mappedHero.find(val => val.levelMin <= hero.stats.level && val.levelMax >= hero.stats.level);
    if (stats) {
      return geUriForName(
        `${HEROES_CLASSES_ICONS[hero.meta.heroClass].toUpperCase()}_${stats.levelMin}_NEUTRAL_AVATAR`,
      );
    } else {
      return geUriForName(`${HEROES_CLASSES_ICONS[hero.meta.heroClass].toUpperCase()}_1_AVATAR`);
    }
  }
  return hero.meta.uri;
}

export function prepareHeroImageUrl(hero: HeroEntity): string {
  const mappedHero = heroesMap.get(hero.meta.heroClass);
  if (mappedHero) {
    const stats = mappedHero.find(val => val.levelMin <= hero.stats.level && val.levelMax >= hero.stats.level);
    if (stats && hero.stats.level > 4) {
      let image = `${HEROES_CLASSES_ICONS[hero.meta.heroClass].toUpperCase()}_${stats.levelMin}_NEUTRAL_`;

      if (hero.meta.heroClass === HEROES.THRALL) {
        if (hero.core.energy > stats.stats.ENG) image += `ENG`;
        else if (hero.core.dexterity > stats.stats.DEX) image += `DEX`;
        else if (hero.core.strength > stats.stats.STR) image += `STR`;
        else image += `VIT`;
      }

      if (hero.meta.heroClass === HEROES.SAVAGE) {
        if (hero.core.energy > stats.stats.ENG) image += `ENG`;
        else if (hero.core.dexterity > stats.stats.DEX) image += `DEX`;
        else if (hero.core.vitality > stats.stats.VIT) image += `VIT`;
        else image += `STR`;
      }

      if (hero.meta.heroClass === HEROES.MAGE) {
        if (hero.core.strength > stats.stats.STR) image += `STR`;
        else if (hero.core.dexterity > stats.stats.DEX) image += `DEX`;
        else if (hero.core.vitality > stats.stats.VIT) image += `VIT`;
        else image += `ENG`;
      }

      if (hero.meta.heroClass === HEROES.ASSASSIN) {
        if (hero.core.energy > stats.stats.ENG) image += `ENG`;
        else if (hero.core.strength > stats.stats.STR) image += `STR`;
        else if (hero.core.vitality > stats.stats.VIT) image += `VIT`;
        else image += `DEX`;
      }

      return geUriForName(image);
    } else {
      return geUriForName(`${HEROES_CLASSES_ICONS[hero.meta.heroClass].toUpperCase()}_1`);
    }
  }
  return hero.meta.uri;
}

type Stats = {
  STR: number;
  DEX: number;
  VIT: number;
  ENG: number;
};

type LevelStats = {
  levelMin: number;
  levelMax: number;
  stats: Stats;
};

const thrallLevelData: LevelStats[] = [
  { levelMin: 5, levelMax: 9, stats: { STR: 15, DEX: 20, VIT: 50, ENG: 10 } },
  { levelMin: 10, levelMax: 14, stats: { STR: 25, DEX: 40, VIT: 50, ENG: 10 } },
  { levelMin: 15, levelMax: 19, stats: { STR: 34, DEX: 40, VIT: 44, ENG: 17 } },
  { levelMin: 20, levelMax: 24, stats: { STR: 39, DEX: 45, VIT: 49, ENG: 21 } },
  { levelMin: 25, levelMax: 100, stats: { STR: 44, DEX: 49, VIT: 54, ENG: 26 } },
];

const savageLevelData: LevelStats[] = [
  { levelMin: 5, levelMax: 9, stats: { STR: 33, DEX: 19, VIT: 28, ENG: 10 } },
  { levelMin: 10, levelMax: 14, stats: { STR: 38, DEX: 32, VIT: 31, ENG: 14 } },
  { levelMin: 15, levelMax: 19, stats: { STR: 64, DEX: 35, VIT: 35, ENG: 14 } },
  { levelMin: 20, levelMax: 24, stats: { STR: 78, DEX: 40, VIT: 40, ENG: 14 } },
  { levelMin: 25, levelMax: 100, stats: { STR: 90, DEX: 60, VIT: 60, ENG: 14 } },
];

const mageLevelData: LevelStats[] = [
  { levelMin: 5, levelMax: 9, stats: { STR: 13, DEX: 18, VIT: 24, ENG: 35 } },
  { levelMin: 10, levelMax: 14, stats: { STR: 17, DEX: 25, VIT: 36, ENG: 37 } },
  { levelMin: 15, levelMax: 19, stats: { STR: 23, DEX: 33, VIT: 42, ENG: 41 } },
  { levelMin: 20, levelMax: 24, stats: { STR: 30, DEX: 36, VIT: 45, ENG: 51 } },
  { levelMin: 25, levelMax: 100, stats: { STR: 48, DEX: 52, VIT: 58, ENG: 61 } },
];

const assassinLevelData: LevelStats[] = [
  { levelMin: 5, levelMax: 9, stats: { STR: 20, DEX: 32, VIT: 21, ENG: 17 } },
  { levelMin: 10, levelMax: 14, stats: { STR: 26, DEX: 43, VIT: 23, ENG: 18 } },
  { levelMin: 15, levelMax: 19, stats: { STR: 35, DEX: 51, VIT: 19, ENG: 20 } },
  { levelMin: 20, levelMax: 24, stats: { STR: 35, DEX: 74, VIT: 22, ENG: 29 } },
  { levelMin: 25, levelMax: 100, stats: { STR: 35, DEX: 85, VIT: 27, ENG: 33 } },
];

const heroesMap: Map<HEROES, LevelStats[]> = new Map<HEROES, LevelStats[]>([
  [HEROES.THRALL, thrallLevelData],
  [HEROES.SAVAGE, savageLevelData],
  [HEROES.MAGE, mageLevelData],
  [HEROES.ASSASSIN, assassinLevelData],
]);
